import React, { useState, useEffect } from 'react';
import { Star, X } from 'lucide-react';
import './RatingModal.css';

export interface RatingModalProps {
  isOpen: boolean;
  courseTitle: string;
  existingRating?: {
    rating: number;
    review: string;
  } | null;
  isSubmitting?: boolean;
  onSubmit: (rating: number, review: string) => void;
  onClose: () => void;
}

const RatingModal: React.FC<RatingModalProps> = ({
  isOpen,
  courseTitle,
  existingRating,
  isSubmitting = false,
  onSubmit,
  onClose
}) => {
  const [rating, setRating] = useState<number>(existingRating?.rating || 0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [review, setReview] = useState<string>(existingRating?.review || '');

  // Update state when existingRating changes
  useEffect(() => {
    if (existingRating) {
      setRating(existingRating.rating);
      setReview(existingRating.review || '');
    } else {
      setRating(0);
      setReview('');
    }
  }, [existingRating, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (rating > 0) {
      onSubmit(rating, review);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="rating-modal-overlay" onClick={onClose}>
      <div className="rating-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rating-modal-header">
          <h3 className="rating-modal-title">
            {existingRating ? 'Update Your Rating' : 'Rate This Course'}
          </h3>
          <button
            className="rating-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="rating-modal-body">
            <p className="rating-modal-course-title">{courseTitle}</p>

            <div className="rating-modal-stars-section">
              <label className="rating-modal-label">Your Rating *</label>
              <div className="rating-modal-stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    className="rating-modal-star-btn"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    aria-label={`Rate ${star} stars`}
                  >
                    <Star
                      size={32}
                      className={`rating-modal-star ${
                        star <= (hoverRating || rating) ? 'filled' : ''
                      }`}
                      fill={star <= (hoverRating || rating) ? '#F59E0B' : 'none'}
                      color="#F59E0B"
                    />
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p className="rating-modal-rating-text">
                  {rating === 1 && 'Poor'}
                  {rating === 2 && 'Fair'}
                  {rating === 3 && 'Good'}
                  {rating === 4 && 'Very Good'}
                  {rating === 5 && 'Excellent'}
                </p>
              )}
            </div>

            <div className="rating-modal-review-section">
              <label htmlFor="review" className="rating-modal-label">
                Your Review (Optional)
              </label>
              <textarea
                id="review"
                className="rating-modal-textarea"
                placeholder="Share your thoughts about this course..."
                value={review}
                onChange={(e) => setReview(e.target.value)}
                rows={5}
                maxLength={500}
              />
              <p className="rating-modal-char-count">
                {review.length}/500 characters
              </p>
            </div>
          </div>

          <div className="rating-modal-footer">
            <button
              type="button"
              className="rating-modal-btn rating-modal-btn-cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rating-modal-btn rating-modal-btn-submit"
              disabled={rating === 0 || isSubmitting}
            >
              {isSubmitting
                ? (existingRating ? 'Updating...' : 'Submitting...')
                : (existingRating ? 'Update Rating' : 'Submit Rating')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RatingModal;
